import { AxiosResponse } from 'axios';
import { DEVICE_INFO_API_URL } from '../../../config';
import deviceInfoAxios from '../../../utils/axios/deviceInfo.axios';

export type FeedbackItem = {
  _id: string;
  description?: string;
  type?: 'staff' | 'guest';
  action?: boolean;
  actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
  createdAt?: string;
  additionalInfo?: {
    sentiment?: 'positive' | 'negative' | 'neutral';
    sentimentScore?: number;
    tripId?: string;
    description?: string;
    category?: string;
    subCategory?: string | null;
    importance?: 'High' | 'Medium' | 'Low';
  };
};

export type PageInfo = {
  totalItems: number;
  page: number;
  size: number;
  totalPages: number;
};

export type ListByTripResponse = {
  data: {
    items: FeedbackItem[];
    pageInfo: PageInfo;
  };
};

const listByTrip = (
  tripId: string,
  page: number,
  size: number,
): Promise<AxiosResponse<ListByTripResponse>> => {
  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/trip/${tripId}?page=${page}&size=${size}`;
  return deviceInfoAxios.get(url);
};

const updateDescription = (id: string, description: string) => {
  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/${id}/description`;
  return deviceInfoAxios.put(url, { description });
};

const deleteFeedback = (id: string) => {
  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/${id}`;
  return deviceInfoAxios.delete(url);
};

const exportAllForTrip = (
  tripId: string,
  params?: {
    type?: 'staff' | 'guest';
    sentiment?: 'positive' | 'negative' | 'neutral';
    actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
    action?: boolean;
    startDate?: string;
    endDate?: string;
  },
) => {
  const q = new URLSearchParams();
  if (params?.type) q.set('type', params.type);
  if (params?.sentiment) q.set('sentiment', params.sentiment);
  if (params?.actionStatus) q.set('actionStatus', params.actionStatus);
  if (typeof params?.action === 'boolean') q.set('action', String(params.action));
  if (params?.startDate) q.set('startDate', params.startDate);
  if (params?.endDate) q.set('endDate', params.endDate);

  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/trip/${tripId}/export${q.toString() ? `?${q.toString()}` : ''}`;
  return deviceInfoAxios.get(url);
};

const createForTrip = (tripId: string, payload: {
  description: string;
  type?: 'staff' | 'guest';
  sentiment?: 'positive' | 'negative' | 'neutral';
  action?: boolean;
  actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
  originalMessage?: string;
  additionalInfo?: Record<string, any>;
  files?: Array<{ name: string; url: string }>;
}) => {
  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/trip/${tripId}`;
  return deviceInfoAxios.post(url, payload);
};

/** ✅ NEW: delete one assignedCabin from a trip */
const deleteAssignedCabin = (tripId: string, assignedCabinId: string) => {
  const url = `${DEVICE_INFO_API_URL}/trips/${tripId}/assigned-cabins/${assignedCabinId}`;
  return deviceInfoAxios.delete(url);
};

/** ✅ NEW: update feedback importance (Admin only) */
const updateImportance = (
  id: string,
  payload: {
    importance: 'High' | 'Medium' | 'Low';
    originalImportance?: 'High' | 'Medium' | 'Low';
  }
) => {
  const url = `${DEVICE_INFO_API_URL}/rebecca-ai-agent/feedbacks/${id}/importance`;
  return deviceInfoAxios.put(url, payload);
};

export const AdminFeedbacksApi = {
  listByTrip,
  updateDescription,
  deleteFeedback,
  exportAllForTrip,
  createForTrip,
  updateImportance,
};

/** ✅ Export trips API alongside (kept separate for clarity) */
export const AdminTripsApi = {
  deleteAssignedCabin,
};
